/**
 * AccountRfcWizard — generates the Request for Coverage for an account.
 *
 * Mirrors the deals-flow ConvertToDealWizard: a review → configure → success
 * modal. Surfaced via the "Generate RFC" action. Generating the RFC normally
 * creates the access link/password only — it does not move the account's stage,
 * so an RFC can be generated at any point in the process. The one exception is
 * when the SWOT call was skipped: there is no intermediate step to land on, so
 * generating the RFC advances the account straight to RFC Draft (see
 * `accountsRouter.startRfc`).
 */

import React from 'react';

import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@common-web/components/ui/dialog';
import {Separator} from '@common-web/components/ui/separator';
import {AssignedBrokerLink} from '@common-web/components/account/AssignedBrokerLink';

import {ArrowRight, CheckCircle, FileText, Loader2} from 'lucide-react';

import type {AccountsSwotStatus, LifecyclePhase} from '@api/generated/client';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {findPositionBySystemSubStage} from '@common/accountsView';
import {generateRandomPassword} from '@common/lib/utils';
import {toast} from '@common-web/hooks/use-toast';

interface AccountRfcWizardProps {
  account: {
    id: string;
    companyName: string;
    state: string | null;
    estimatedFtEmployees: number | null;
    estimatedPtEmployees: number | null;
    assignedBroker: {id: string; name: string} | null;
    swotStatus: AccountsSwotStatus | null;
    lifecyclePhase: LifecyclePhase;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerated?: () => void;
}

export function AccountRfcWizard({
  account,
  open,
  onOpenChange,
  onGenerated,
}: AccountRfcWizardProps): React.ReactElement {
  const utils = trpc.useUtils();

  // Generating the RFC normally leaves the stage untouched, but when the SWOT
  // call was skipped the backend advances the account straight to RFC Draft.
  const advancesToDraft = account.swotStatus === 'SKIPPED' && account.lifecyclePhase === 'LEAD';

  // Tenant-configured display name for the SWOT_CALL sub-stage, so copy tracks
  // any rename made in the accounts-view editor.
  const config = useAccountsViewConfig();
  const swotLabel =
    findPositionBySystemSubStage(config, 'SWOT_CALL')?.subStage.label ?? 'SWOT Call';

  const [step, setStep] = React.useState<'review' | 'configure' | 'success'>('review');
  const [password, setPassword] = React.useState(generateRandomPassword());
  const [section125FormEnabled, setSection125FormEnabled] = React.useState(true);
  const [generatedPassword, setGeneratedPassword] = React.useState<string | null>(null);

  // Reset wizard state whenever it's (re)opened.
  React.useEffect(() => {
    if (open) {
      setStep('review');
      setPassword(generateRandomPassword());
      setSection125FormEnabled(true);
      setGeneratedPassword(null);
    }
  }, [open]);

  const startRfc = trpc.account.startRfc.useMutation({
    onSuccess: (result) => {
      setGeneratedPassword(result.plaintextPassword);
      setStep('success');
      utils.account.getById.invalidate({accountsId: account.id});
      utils.account.getActivity.invalidate({accountsId: account.id});
    },
    onError: createMutationErrorHandler('Failed to generate RFC'),
  });

  const handleGenerate = (): void => {
    if (!section125FormEnabled) return;
    startRfc.mutate({
      accountsId: account.id,
      password,
      section125FormEnabled,
      mecFormEnabled: false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <FileText className='h-5 w-5 text-primary' />
            Generate RFC
          </DialogTitle>
        </DialogHeader>

        {step === 'review' && (
          <div className='space-y-4'>
            <p className='text-sm text-muted-foreground'>
              Review the account details before generating the Request for Coverage. This creates
              the access link and password
              {advancesToDraft
                ? ` and, since the ${swotLabel} was skipped, moves the account to RFC Draft.`
                : ' without changing the account\u2019s stage.'}
            </p>
            <div className='grid grid-cols-2 gap-3 rounded-lg bg-muted/40 p-4'>
              <div>
                <p className='text-xs text-muted-foreground'>Company</p>
                <p className='text-sm font-medium'>{account.companyName}</p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground'>State</p>
                <p className='text-sm font-medium'>{account.state || '\u2014'}</p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground'>FT Employees</p>
                <p className='text-sm font-medium'>{account.estimatedFtEmployees ?? '\u2014'}</p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground'>PT Employees</p>
                <p className='text-sm font-medium'>{account.estimatedPtEmployees ?? '\u2014'}</p>
              </div>
              <div>
                <p className='text-xs text-muted-foreground'>Broker</p>
                <AssignedBrokerLink broker={account.assignedBroker} className='text-sm font-medium' />
              </div>
            </div>
            <div className='flex justify-end'>
              <Button onClick={() => setStep('configure')}>
                Continue
                <ArrowRight className='ml-1 h-4 w-4' />
              </Button>
            </div>
          </div>
        )}

        {step === 'configure' && (
          <div className='space-y-4'>
            <p className='text-sm text-muted-foreground'>
              Configure the Request for Coverage for this account.
            </p>

            <div>
              <Label>Access Password *</Label>
              <div className='flex gap-2'>
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='font-mono'
                />
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => setPassword(generateRandomPassword())}
                >
                  Generate
                </Button>
              </div>
              <p className='mt-1 text-xs text-muted-foreground'>
                Password for the company to access the RFC form.
              </p>
            </div>

            <Separator />

            <div>
              <Label>Forms *</Label>
              <div className='mt-2 flex flex-col space-y-2.5'>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='section125'
                    checked={section125FormEnabled}
                    onCheckedChange={(c) => setSection125FormEnabled(!!c)}
                  />
                  <Label htmlFor='section125' className='mb-0'>
                    125 Program Form
                  </Label>
                </div>
              </div>
              {!section125FormEnabled && (
                <p className='mt-1 text-xs text-red-500'>125 Program form must be enabled</p>
              )}
            </div>

            <div className='flex justify-between'>
              <Button variant='outline' onClick={() => setStep('review')}>
                Back
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={startRfc.isPending || !section125FormEnabled || password.length < 6}
              >
                {startRfc.isPending ? (
                  <>
                    <Loader2 className='mr-1 h-4 w-4 animate-spin' />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className='mr-1 h-4 w-4' />
                    Generate RFC
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className='space-y-4 py-2 text-center'>
            <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100'>
              <CheckCircle className='h-6 w-6 text-green-600' />
            </div>
            <div>
              <h3 className='text-lg font-semibold'>RFC Generated</h3>
              <p className='mt-1 text-sm text-muted-foreground'>
                {advancesToDraft
                  ? 'Share the access link and password from the RFC tab. The account has moved to RFC Draft.'
                  : 'Share the access link and password from the RFC tab. The account\u2019s stage is unchanged \u2014 advance it when you\u2019re ready.'}
              </p>
            </div>
            {generatedPassword ? (
              <div className='space-y-1 rounded-md border border-green-200 bg-green-50 p-3 text-left'>
                <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                  Access password
                </p>
                <div className='flex items-center justify-between gap-2'>
                  <span className='font-mono font-bold'>{generatedPassword}</span>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPassword);
                      toast({title: 'Password copied'});
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            ) : null}
            <Button
              className='w-full'
              onClick={() => {
                onOpenChange(false);
                onGenerated?.();
              }}
            >
              <FileText className='mr-1 h-4 w-4' />
              Go to RFC
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
